import { cn } from '../../lib/cn';
import { Text } from './Text';
import { X } from 'lucide-react';

export interface BannerProps {
  children: React.ReactNode;
  variant?: 'info' | 'success' | 'warning' | 'error' | 'primary';
  icon?: React.ReactNode;
  action?: React.ReactNode;
  onDismiss?: () => void;
  className?: string;
}

const variantClasses: Record<NonNullable<BannerProps['variant']>, string> = {
  info: 'bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-900/20 dark:text-sky-100 dark:border-sky-800',
  success: 'bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-100 dark:border-emerald-800',
  warning: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-800',
  error: 'bg-red-50 text-red-900 border-red-200 dark:bg-red-900/20 dark:text-red-100 dark:border-red-800',
  primary: 'bg-park-50 text-park-900 border-park-200 dark:bg-park-900/20 dark:text-park-100 dark:border-park-800',
};

const iconContainerClasses: Record<NonNullable<BannerProps['variant']>, string> = {
  info: 'bg-sky-100 text-sky-600 dark:bg-sky-800 dark:text-sky-300',
  success: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-800 dark:text-emerald-300',
  warning: 'bg-amber-100 text-amber-600 dark:bg-amber-800 dark:text-amber-300',
  error: 'bg-red-100 text-red-600 dark:bg-red-800 dark:text-red-300',
  primary: 'bg-park-100 text-park-600 dark:bg-park-800 dark:text-park-300',
};

export function Banner({
  children,
  variant = 'info',
  icon,
  action,
  onDismiss,
  className,
}: BannerProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border p-3.5',
        variantClasses[variant],
        className
      )}
      role="status"
    >
      {icon && (
        <div
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            iconContainerClasses[variant]
          )}
        >
          {icon}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <Text as="div" variant="body-medium" color="primary" className="text-inherit">
          {children}
        </Text>
        {action && <div className="mt-2">{action}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-md p-1 text-current opacity-70 hover:opacity-100 focus-visible:ring-2 touch-target"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
